import { CollectionPreferencesProps, Link, SelectProps, TableProps } from '@amzn/awsui-components-core';
import React from 'react';
import { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import AppNameWithLink from 'src/components/generic-components/AppNameWithLink';
import { createTableSortLabelFn } from 'src/i18n-strings/table';
import { getReadableDateTimeFormat } from 'src/utilities/DateTimeUtilities';

export const SEARCHABLE_COLUMNS: (keyof ApplicationFlatTableEntity)[] = [
  'applicationName',
  'shortApplicationDescription',
  'applicationOwner',
  'glAccount',
  'toolUI'
];

export const CONTENT_DISPLAY_OPTIONS: CollectionPreferencesProps.ContentDisplayOption[] = [
  { id: 'applicationName', label: 'Application', alwaysVisible: true },
  { id: 'shortApplicationDescription', label: 'Description' },
  { id: 'applicationOwner', label: 'Team' },
  { id: 'glAccount', label: 'Module' },
  { id: 'toolUI', label: 'Product UI' },
  { id: 'updatedBy', label: 'Updated By' },
  { id: 'updatedTime', label: 'Updated At' }
];

export const VISIBLE_CONTENT_OPTIONS: CollectionPreferencesProps.VisibleContentOptionsGroup[] = [
  {
    label: 'Properties',
    options: [
      { id: 'applicationName', label: 'Application', editable: false },
      { id: 'shortApplicationDescription', label: 'Description', editable: false },
      { id: 'applicationOwner', label: 'Team', editable: true },
      { id: 'glAccount', label: 'Module', editable: true },
      { id: 'toolUI', label: 'Product UI', editable: true },
      { id: 'updatedBy', label: 'Updated By', editable: true },
      { id: 'updatedTime', label: 'Updated At', editable: true }
    ]
  }
];

export const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 50,
  contentDisplay: [
    { id: 'applicationName', visible: true },
    { id: 'shortApplicationDescription', visible: true },
    { id: 'applicationOwner', visible: true },
    { id: 'glAccount', visible: true },
    { id: 'toolUI', visible: true },
    { id: 'updatedBy', visible: false },
    { id: 'updatedTime', visible: false }
  ],
  wrapLines: false,
  stripedRows: false,
  contentDensity: 'comfortable'
};

const rawColumns: TableProps.ColumnDefinition<ApplicationFlatTableEntity>[] = [
  {
    id: 'applicationName',
    header: 'Application',
    width: 230,
    cell: (item) => (
      <AppNameWithLink
        appName={item.applicationName}
        appId={item.id}
        embeddingEnabled={item.embeddingEnabled}
        applicationLink={item.applicationLink}
        urlName={item.urlName}
      />
    ),
    sortingField: 'applicationName'
  },
  {
    id: 'shortApplicationDescription',
    header: 'Description',
    cell: (item) => item.shortApplicationDescription,
    sortingField: 'shortApplicationDescription'
  },
  {
    id: 'applicationOwner',
    header: 'Team',
    width: 150,
    cell: (item) => item.applicationOwner,
    sortingField: 'applicationOwner'
  },
  {
    id: 'glAccount',
    header: 'Module',
    width: 250,
    cell: (item) => item.glAccount,
    sortingField: 'glAccount'
  },
  {
    id: 'toolUI',
    header: 'Product UI',
    width: 175,
    cell: (item) => item.toolUI,
    sortingField: 'toolUI'
  },
  {
    id: 'updatedBy',
    header: 'Updated By',
    width: 150,
    cell: (item) => item.updatedBy,
    sortingField: 'updatedBy'
  },
  {
    id: 'updatedTime',
    header: 'Updated At',
    width: 150,
    cell: (item) => getReadableDateTimeFormat(item.updatedTime),
    sortingField: 'updatedTime'
  }
];

export const COLUMN_DEFINITIONS = rawColumns.map((column) => ({
  ...column,
  ariaLabel: createTableSortLabelFn(column as TableProps.ColumnDefinition<unknown>)
}));

export const DEFAULT_TEAM: SelectProps.Option = { value: '0', label: 'Any Team' };
export const DEFAULT_MODULE: SelectProps.Option = { value: '0', label: 'Any Module' };

export const FILTER_LABEL_TEAM = 'Filter by Team';
export const FILTER_LABEL_MODULE = 'Filter by Module';

export function getFilteredHeaderCounterText(filteredCount: number, totalCount: number): string {
  if (filteredCount < totalCount) {
    return `(${filteredCount}/${totalCount})`;
  }
  return `(${totalCount})`;
}

export function prepareSelectOptions(
  items: ApplicationFlatTableEntity[],
  field: keyof ApplicationFlatTableEntity,
  defaultOption: SelectProps.Option
): SelectProps.Options {
  const optionSet = new Set<string>();
  items.forEach((item) => {
    const value = item[field];
    if (typeof value === 'string' && value && value !== 'N/A') {
      optionSet.add(value);
    }
  });
  const sorted = Array.from(optionSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return [defaultOption, ...sorted.map((item, index) => ({ label: item, value: (index + 1).toString() }))];
}

export function matchesTeam(item: ApplicationFlatTableEntity, selectedTeam: SelectProps.Option) {
  return selectedTeam.value === DEFAULT_TEAM.value || item.applicationOwner === selectedTeam.label;
}

export function matchesModule(item: ApplicationFlatTableEntity, selectedModule: SelectProps.Option) {
  if (selectedModule.value === DEFAULT_MODULE.value) return true;
  if (selectedModule.label === 'Other') return !item.glAccount || item.glAccount === 'N/A';
  return item.glAccount === selectedModule.label;
}
